import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Filtri applicati con un tasto «Cerca»: i controlli modificano la bozza, le
 * chiamate al backend leggono solo `applicati`, che cambia quando si chiama
 * `applica()`. Cosi' cambiare tre filtri non fa tre letture.
 *
 * La pagina parte vuota: `cercato` resta false finche' l'utente non preme
 * «Cerca», e le pagine non leggono nulla prima. Sono letture lente (GO) che
 * col loader bloccherebbero la pagina appena aperta.
 *
 * `automatico` e' un valore che, quando cambia, allinea subito i filtri senza
 * aspettare il tasto: tipicamente la societa' scelta in topbar, che e' il
 * contesto della pagina e non un filtro. Prima della prima ricerca si allinea
 * e basta; dopo, rilegge, perche' i dati a video sarebbero di un altro contesto.
 *
 * Ogni `applica()` produce un oggetto nuovo: «Cerca» con gli stessi filtri
 * rilegge comunque i dati.
 */
export function useFiltriApplicati<T extends object>(bozza: T, automatico?: unknown) {
  // versione: cresce a ogni lettura, per le chiamate che dipendono da valori
  // primitivi (uguali a prima) e devono comunque ripartire
  const [stato, setStato] = useState<{ applicati: T; versione: number; cercato: boolean }>(
    { applicati: bozza, versione: 0, cercato: false },
  );
  const bozzaRef = useRef(bozza);
  bozzaRef.current = bozza;

  const applica = useCallback(() => setStato((s) => ({
    applicati: { ...bozzaRef.current }, versione: s.versione + 1, cercato: true,
  })), []);
  const { applicati, versione, cercato } = stato;

  const precedente = useRef(automatico);
  useEffect(() => {
    if (Object.is(precedente.current, automatico)) return;
    precedente.current = automatico;
    setStato((s) => ({
      applicati: { ...bozzaRef.current },
      versione: s.cercato ? s.versione + 1 : s.versione,
      cercato: s.cercato,
    }));
  }, [automatico]);

  const modificati = JSON.stringify(bozza) !== JSON.stringify(applicati);

  return { applicati, versione, applica, modificati, cercato };
}
